import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import { WebAuth } from 'auth0-js'
import jwtDecode from 'jwt-decode'
import { getUrlPath } from '../utils/helpers'
import jwt_decode from 'jwt-decode'

const DEFAULT_STATE = {
  accessToken: null,
  idToken: null,
  expiresAt: 0
}

const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID
const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN

const KEY_LOGGED_IN = 'AUTH0_IsLoggedIn'
const KEY_AUTH0 = 'AUTH0'
const KEY_REDIRECT = 'AUTH0_Redirect'

const Auth0Context = createContext()
const useAuth0Context = () => useContext(Auth0Context)

const createAuth0 = () => new WebAuth({
  domain: AUTH0_DOMAIN,
  clientID: AUTH0_CLIENT_ID,
  redirectUri: `${window.location.origin}`,
  responseType: 'token id_token',
  scope: 'openid profile email'
});

export const getAuthState = () => {
  return JSON.parse(localStorage.getItem('AUTH0'))
}

const useAuthState = () => {
  const state = getAuthState() || DEFAULT_STATE

  return useState(state)
}

const useContextValue = () => {
  const [authState, updateAuthState] = useAuthState()

  return {
    auth0: createAuth0(),
    authState,
    updateAuthState
  }
}

export const Auth0Provider = ({ children }) => {
  const ctx = useContextValue()
  return (
    <Auth0Context.Provider value={ctx}>
      {children}
    </Auth0Context.Provider>
  )
}

export const useAuth0 = () => {
  const { auth0, authState, updateAuthState, auth0Lock } = useAuth0Context()

  const fetchCurrentUser = async (state) => {
    const profile = jwt_decode(state.idToken)
    const cleanSub = encodeURI(profile.sub)
    const path = getUrlPath(`getCurrentUserProfile?sub=${cleanSub}`)
    let response = await fetch(path)
    state.user = await response.json()
  }

  const setSession = useCallback(
    async authResult => {
      let expiresAt = authResult.expiresIn * 1000 + new Date().getTime()

      const state = {
        accessToken: authResult.accessToken,
        idToken: authResult.idToken,
        expiresAt: expiresAt
      }
      await fetchCurrentUser(state)

      localStorage.setItem(KEY_LOGGED_IN, true)
      localStorage.setItem(KEY_AUTH0, JSON.stringify(state))

      updateAuthState(state)
    },
    [updateAuthState]
  )

  const login = useCallback(async (username, password) => {
    localStorage.setItem(KEY_REDIRECT, '/')

    const loginPromise = new Promise((resolve, reject) => {
      auth0.client.login({
        realm: 'Username-Password-Authentication', //connection name or HRD domain
        username: username,
        password: password,
      }, (err, authResult) => {
        if (err) {
          reject((err && err.description) || 'Error logging in')

        } else {
          setSession(authResult)

          const path = localStorage.getItem(KEY_REDIRECT)
          resolve(path)
        }
      })
    })

    return loginPromise

  }, [auth0, setSession])

  const logout = useCallback(async () => {
    localStorage.clear()
    localStorage.setItem(KEY_LOGGED_IN, false)

    auth0.logout({ returnTo: `${window.location.origin}` }).then(r => {
      updateAuthState(DEFAULT_STATE)
    })

  }, [auth0, updateAuthState])

  const isLoggedIn = useCallback(
    () => localStorage.getItem(KEY_LOGGED_IN),
    []
  )

  const changePassword = useCallback(async (username) => {
    const changePasswordPromise = new Promise((resolve, reject) => {
      auth0.changePassword({
        connection: 'Username-Password-Authentication',
        email: username
      }, (err, resValue) => {
        if (err) {
          reject((err && err.description) || 'Error sending password reset request.')
        } else {
          resolve(resValue)
        }
      })
    })

    return changePasswordPromise
  }, [auth0])

  const isAuthenticated = useMemo(
    () => new Date().getTime() < authState.expiresAt,
    [authState]
  )

  const profile = useMemo(
    () => authState && authState.idToken && jwtDecode(authState.idToken),
    [authState]
  )

  const user = useMemo(() => authState && authState.user , [authState])

  const jwt = useMemo(
    () => authState && authState.idToken,
    [authState]
  )

  const handleAuthentication = useCallback(() => {
    auth0.parseHash((err, res) => {
      if (res && res.accessToken && res.idToken) {
        if (authState.isLoggingOut !== true) {
          setSession(res)
        }

        const path = localStorage.getItem(KEY_REDIRECT)

        if (path) {
          window.location.replace(path)
        } else {
          window.location.replace('/login')
        }
      } else if (err) {
        console.error(err)
      } else {
        console.log('no res and no err')
      }
    })

  }, [auth0, setSession, authState])

  return {
    login,
    logout,
    isLoggedIn,
    isAuthenticated,
    handleAuthentication,
    profile,
    user,
    jwt,
    auth0Lock,
    changePassword
  }
}